<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { checkInvoiceHasAccountingAddon } from '@/api/subscriptionManagerClient'
import { Alert, Button, Card, Spinner } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { track } from '@/lib/usageTracker'
import { setupFlowSteps } from './setupFlow'

const route = useRoute()
const router = useRouter()
const store = useAuthStore()

const loading = ref(false)
const missingParams = ref(false)
const apiError = ref(false)
const networkError = ref(false)
const apiErrorTitle = ref('Verification Failed')
const apiErrorMessage = ref('We could not verify your invoice details. This might be due to a temporary issue.')
const invoiceNumber = computed(() => route.query.invoiceNumber?.toString() || '')
const storeID = computed(() => route.query.storeID?.toString() || route.query.storeId?.toString() || '')
const contextItems = computed(() => [
  { label: 'Invoice reference', value: invoiceNumber.value || 'Missing from invitation link', tone: invoiceNumber.value ? 'default' : 'warning' },
  { label: 'Store reference', value: storeID.value || 'Missing from invitation link', tone: storeID.value ? 'default' : 'warning' },
  { label: 'Verification state', value: missingParams.value ? 'Action needed' : loading.value ? 'Checking invoice add-on' : 'Awaiting verification', tone: missingParams.value ? 'warning' : loading.value ? 'accent' : 'default' },
])

async function verify() {
  track('form:VerifyInvoice', 'submit')
  resetUI()

  store.setQueryParams({ invoiceNumber: invoiceNumber.value, storeID: storeID.value })

  if (!invoiceNumber.value || !storeID.value) {
    missingParams.value = true
    return
  }

  try {
    loading.value = true
    await checkInvoiceHasAccountingAddon(invoiceNumber.value)
    store.setInvoiceHasAddon(true)
    router.replace({ name: 'PackageSelection', query: { invoiceNumber: invoiceNumber.value, storeID: storeID.value } })
  } catch (e) {
    const msg = String((e as Error)?.message || e)
    if (/failed to fetch|network|TypeError/i.test(msg)) {
      networkError.value = true
    } else if (/HTTP\s*\d+/i.test(msg)) {
      apiError.value = true
      apiErrorTitle.value = 'Verification Failed'
      apiErrorMessage.value = msg
    } else {
      apiError.value = true
      apiErrorTitle.value = 'Unexpected Error'
      apiErrorMessage.value = msg
    }
    console.error(e)
  } finally {
    loading.value = false
  }
}

function resetUI() {
  loading.value = false
  missingParams.value = false
  apiError.value = false
  networkError.value = false
}

onMounted(() => {
  verify()
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Setup intake"
    title="Verify invoice access"
    subtitle="We confirm the invoice add-on and store reference before showing available accounting providers."
    :current-step="1"
    :steps="setupFlowSteps"
    :context-items="contextItems"
  >
    <Card padding="lg" class="setup-panel">
      <div class="setup-panel__actions">
        <Button v-if="!loading && !missingParams" variant="secondary" @click="verify">
          Refresh check
        </Button>
      </div>

      <div class="setup-panel__summary">
        <div class="summary-row">
          <span class="summary-label">Invoice</span>
          <span class="summary-value">{{ invoiceNumber || 'Pending from invite link' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Store</span>
          <span class="summary-value">{{ storeID || 'Pending from invite link' }}</span>
        </div>
      </div>

      <Card v-if="loading" padding="lg" class="setup-state">
        <Spinner size="lg" />
        <div>
          <div class="setup-state__title">Verifying invoice details</div>
          <p class="setup-state__copy">Checking that this invoice is eligible for LYT Accounting and linking it to the correct store context.</p>
        </div>
      </Card>

      <Alert v-else-if="missingParams" id="queryParamsError" variant="warning" title="Invitation details are incomplete">
        This setup link must include both an invoice number and a store ID. Reopen the invitation or contact
        <a href="mailto:support@chrilantech.com" class="support-link">support@chrilantech.com</a>.
      </Alert>

      <Alert v-else-if="networkError" id="networkError" variant="warning" title="Verification service unreachable">
        We couldn't reach the verification service. Check your connection and retry this invoice check.
        <div class="setup-alert__actions">
          <Button @click="verify" id="retryNetworkButton">Retry verification</Button>
        </div>
      </Alert>

      <Alert v-else-if="apiError" id="apiError" variant="error" :title="apiErrorTitle">
        {{ apiErrorMessage }}
        <div class="setup-alert__actions">
          <Button @click="verify" id="retryButton">Try again</Button>
        </div>
      </Alert>

      <Card v-else padding="lg" class="setup-state setup-state--idle">
        <div>
          <div class="setup-state__title">Preparing provider options</div>
          <p class="setup-state__copy">If verification succeeds, we'll move directly to package selection without losing invoice or store provenance.</p>
        </div>
      </Card>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.setup-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.setup-panel__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.summary-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.setup-panel__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.summary-row {
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.summary-value {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--color-ink);
}

.setup-state {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-height: 190px;
}

.setup-state--idle {
  align-items: flex-start;
}

.setup-state__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-ink);
}

.setup-state__copy {
  margin: 0.35rem 0 0;
  max-width: 54ch;
  color: var(--color-ink-soft);
  font-size: 0.9rem;
}

.setup-alert__actions {
  margin-top: 0.75rem;
}

@media (max-width: 640px) {
  .setup-panel {
    gap: 0.8rem;
  }

  .setup-panel__header,
  .setup-panel__summary {
    flex-direction: column;
  }

  .setup-panel__summary {
    display: flex;
    gap: 0.55rem;
  }

  .summary-row {
    padding: 0.65rem;
  }

  .setup-state {
    min-height: 0;
    align-items: flex-start;
    padding: 0.9rem;
  }

  .setup-alert__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
